import React, { createContext, useState, useEffect } from "react";
import { useQuery } from '@tanstack/react-query';
import ReactPaginate from "react-paginate";

import { prepareOptionsForLocationSearch } from "../../src/js/helpers";
import { getLocations, getMarkers } from '../../api/location';
import Breadcrumbs from '../SharedComponents/Breadcrumbs';
import TopMenu from "../SharedComponents/TopMenu";
import SvgIcon from "../SharedComponents/Icons";
import NotificationPopup from "../SharedComponents/NotificationPopup";
import PlacesList from "./PlacesList";
import MapSection from "./MapSection";
import Filters from "./Filters";

import "./index.sass";


export const Context = createContext({});


const ListView = (props) => {

  const {
    district,
    facilitiesOptions,
    googleApiKey,
    mainCategories,
    pageTitle,
    region,
    regions,
    suitableOptions,
    topMenu,
  } = props;

  const [mapIsOpen, setMapIsOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [numberOfGuests, setNumberOfGuests] = useState('');
  const [selectedSuitableKinds, setSelectedSuitableKinds] = useState([]);
  const [selectedPrice, setSelectedPrice] = useState({ min: 0, max: 500 });
  const [priceSelector, setPriceSelector] = useState({ min: 0, max: 500 });
  const [selectedFacilities, setSelectedFacilities] = useState([]);
  const [hoveredPlaceId, setHoveredPlaceId] = useState(null);
  const [selectedTypes, setSelectedTypes] = useState(null);
  const [boundsTrigger, setBoundsTrigger] = useState(false);
  const [coords, setCoords] = useState({});
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [ownCatering, setOwnCatering] = useState(false);
  const [requestError, setRequestError] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);


  const { data: placesData, isLoading }  = useQuery({
    queryKey: [
      'locations',
      coords,
      ownCatering,
      page,
      selectedSuitableKinds,
      selectedPrice,
      selectedTypes,
      numberOfGuests,
      selectedFacilities,
      selectedRegion,
      selectedDistrict,
    ],
    queryFn: () => getLocations({
      coords,
      ownCatering,
      page,
      selectedSuitableKinds,
      selectedPrice,
      selectedTypes,
      numberOfGuests,
      selectedFacilities,
      selectedRegion,
      selectedDistrict,
      onSuccess: () => initialLoad && setInitialLoad(false),
    }),
    enabled: !!dataLoaded,
    refetchOnWindowFocus: false,
    options: { keepPreviousData: true },
  });


  const { data: markers, isSuccess, isLoading: markersLoading } = useQuery({
    queryKey: [
      'markers',
      coords,
      ownCatering,
      selectedSuitableKinds,
      selectedPrice,
      selectedTypes,
      numberOfGuests,
      selectedFacilities,
      selectedRegion,
      selectedDistrict,
    ],
    queryFn: () => getMarkers({
      coords,
      ownCatering,
      selectedSuitableKinds,
      selectedPrice,
      selectedTypes,
      numberOfGuests,
      selectedFacilities,
      selectedRegion,
      selectedDistrict,
      onSuccess: () => mapIsOpen && setBoundsTrigger(true),
    }),
    enabled: !!dataLoaded,
    refetchOnWindowFocus: false,
    options: { keepPreviousData: true },
  });


  useEffect(() => {
    window.onpopstate = (e) => onPopState(e);

    if (window.innerWidth < 768) {
      setMapIsOpen(false);
    }
  }, []);


  useEffect(() => {
    parseUrlParams();
  }, []);


  const selectedAreaLabel = React.useMemo(
    () => {
      let areaName = 'Denmark';

      selectedDistrict
        ? regions.find(region => region.districts.find(district => district.slug === selectedDistrict && (areaName = district.name)))
        : selectedRegion
          ? regions.find(region => region.slug === selectedRegion && (areaName = region.name))
          : null;

      return areaName;
    },
    [selectedRegion, selectedDistrict],
  );


  const generatePaginationLink = (page) => {
    if (window.location) {
      const { origin, pathname } = window.location;

      return `${origin}${pathname}${page > 1 ? `?page=${page}` : ''}`;
    };

    return null;
  };


  const onPopState = () => {
    const state = (window.history && window.history.state) || null;
    const breadcrumbs = (state && state.breadcrumbs) || null;
    const selectedRegionSlug = (state && state.selectedRegionSlug) || null;
    const selectedDistrictSlug = (state && state.selectedDistrictSlug) || null;

    parseUrlParams({ selectedRegionSlug, selectedDistrictSlug }, breadcrumbs);
  };


  const parseUrlParams = (locationData = {}, breadcrumbs) => {
    const { selectedRegionSlug, selectedDistrictSlug } = locationData;
    const urlSearchParams = new URLSearchParams(window.location.search);
    const selectedSuitable = urlSearchParams.get('suitable_for_kinds');
    const selectedFacilitiesList = urlSearchParams.get('facilities');
    const priceFrom = urlSearchParams.get('price_per_seat_from');
    const priceTo = urlSearchParams.get('price_per_seat_to');
    const categories = urlSearchParams.get('categories');
    const guests = urlSearchParams.get('guest_capacity');
    const catering = urlSearchParams.get('permits_own_catering');
    const districtSlug = selectedDistrictSlug || (district && district.slug) || null;
    const regionSlug = selectedRegionSlug || (region && region.slug) || null;

    setSelectedDistrict(districtSlug);
    setSelectedRegion(regionSlug);
    setPage(urlSearchParams.get('page') || 1);
    setSelectedSuitableKinds(selectedSuitable ? selectedSuitable.split(',').map(Number) : []);
    setSelectedFacilities(selectedFacilitiesList ? selectedFacilitiesList.split(',').map(Number) : []);
    setSelectedTypes(categories ? categories.split(',').map(Number) : []);
    setNumberOfGuests(guests);
    setSelectedPrice({min: priceFrom ? Number(priceFrom) : 0, max: priceTo ? Number(priceTo) : 500 });
    setPriceSelector({min: priceFrom ? Number(priceFrom) : 0, max: priceTo ? Number(priceTo) : 500 });
    setBreadcrumbs(breadcrumbs || generateBreadcrumbs(region, district));
    setOwnCatering(!!catering);
    if (priceFrom || priceTo) {
      setSelectedPrice({min: priceFrom ? Number(priceFrom) : 0, max: priceTo ? Number(priceTo) : 500 });
      setPriceSelector({min: priceFrom ? Number(priceFrom) : 0, max: priceTo ? Number(priceTo) : 500 });
    }
    coords && setCoords({});
    setDataLoaded(true);
  };


  const generateBreadcrumbs = (region, district) => {
    const newBreadcrumbs = [
      { label: 'Hjem', path: '/' },
      { label: 'Leje', path: '/leje' },
    ];

    region && newBreadcrumbs.push({ label: region.name, path: `/leje/${region.slug}` });
    district && district.name && newBreadcrumbs.push({ label: district.name, path: '' });
    return newBreadcrumbs;
  };


  const updateUrlParameters = (paramsToUpdate) => {
    const currentUrl = window.location.href;
    const url = new URL(currentUrl);
    const queryParams = new URLSearchParams(url.search);

    Object.entries(paramsToUpdate).forEach(([key, value]) => {
      if (queryParams.has(key)) {
        const hasLength = typeof value !== 'object' || !!value.length;

        (value && hasLength) ? queryParams.set(key, value) : queryParams.delete(key);
      } else {
        value && queryParams.append(key, value);
      }
    });

    const queryString = queryParams.toString();

    window.history.pushState({ path: url.toString() }, '', `${url.pathname}${queryString.length ? '?' : ''}${queryString}`);
  };


  const handlePageClick = (data) => {
    setTimeout(() => window.scrollTo(0, 0), 10);
    updateUrlParameters({ page: data.selected + 1 === 1 ? '' : data.selected + 1 });
    setPage(data.selected + 1)
  };


  const totalCount = placesData ? placesData.meta.total : 0;
  const perPage = 12;
  const priceMin = 0;
  const priceMax = 500;
  const showPagination = !!(totalCount > perPage);
  const regionsOptions = prepareOptionsForLocationSearch(regions);
  const places = !isLoading && (placesData ? placesData.data : null);

  const values = {
    boundsTrigger,
    coords,
    dataLoaded,
    facilitiesOptions,
    googleApiKey,
    hoveredPlaceId,
    isLoading,
    isSuccess,
    mainCategories,
    mapIsOpen,
    markers,
    markersLoading,
    numberOfGuests,
    ownCatering,
    places,
    priceMax,
    priceMin,
    priceSelector,
    regionsOptions,
    selectedFacilities,
    selectedSuitableKinds,
    selectedPrice,
    selectedTypes,
    setBoundsTrigger,
    setBreadcrumbs,
    setCoords,
    setHoveredPlaceId,
    setNumberOfGuests,
    setMapIsOpen,
    setOwnCatering,
    setPage,
    setPriceSelector,
    setRequestError,
    setSelectedDistrict,
    setSelectedFacilities,
    setSelectedPrice,
    setSelectedSuitableKinds,
    setSelectedTypes,
    setSelectedRegion,
    suitableOptions,
    generateBreadcrumbs,
    updateUrlParameters,
  };


  return (
      <div className="list-view">
        <Context.Provider value={values}>
          <TopMenu {...topMenu} categories={mainCategories} regionsOptions={regionsOptions} />
          <div className="list-view__container">

            <div className={`list-view__content-wrapper ${mapIsOpen ? 'with-map' : ''}`}>

              <Breadcrumbs
                breadcrumbs={breadcrumbs}
                className={mapIsOpen ? 'mobile-hidden' : ''}
              />

              <Filters />

              <h1>
                { initialLoad
                  ? pageTitle
                  : `${totalCount} ${totalCount === 1 ? 'Selskabslokale' : 'Selskabslokaler'}: ${selectedAreaLabel}`
                }
              </h1>

              <PlacesList />

              { showPagination &&
                <div className={`list-view__pagination-wrapper ${mapIsOpen ? 'with-map' : ''}`}>
                  <ReactPaginate
                    activeClassName={"active"}
                    breakLabel={"..."}
                    containerClassName={"pagination"}
                    hrefBuilder={(page) => generatePaginationLink(page)}
                    marginPagesDisplayed={1}
                    forcePage={page - 1}
                    nextLabel={<SvgIcon name="keyboard-arrow-right-rounded-ios" size={24} />}
                    previousLabel={<SvgIcon name="keyboard-arrow-left-rounded-ios" size={24} />}
                    onPageChange={handlePageClick}
                    pageCount={Math.ceil(totalCount / perPage)}
                    pageRangeDisplayed={2}
                  />
                </div>
              }
            </div>

            <MapSection />

            <NotificationPopup
              customClass={'error'}
              handleClose={() => setRequestError(false)}
              message={'Noget gik galt. Prøv venligst senere eller kontakt os hvis fejlen opstår igen.'}
              visible={requestError} />

          </div>
        </Context.Provider>
      </div>
  );
};

export default ListView;
